import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { forkJoin, Observable } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";

import { InteractionTrackingService, InteractionType } from "../interaction-tracking.service";
import { OnboardingStep, OnboardingStepService } from "../onboarding-step.service";
import { PaymentsService } from "../payments.service";

/** Milestone rows no single lesson owns (OnboardingStepKeys.Milestones on the API). */
export const SCORE_PLAN_RELATIONSHIP_RESOLVED_KEY = "score_plan_relationship_resolved";
export const CUSTOM_CARD_SAVED_KEY = "custom_card_saved";
export const CORE_JOURNEY_COMPLETE_KEY = "core_journey_complete";

/** The four keys of the spec §5 core-completion predicate, in journey order. */
export const CORE_JOURNEY_KEYS = [
  "score_first_submitted",
  "buffer_explanation_presented",
  "score_plan_relationship_resolved",
  "qualifying_first_plan_saved",
] as const;

export type CoreJourneyKey = (typeof CORE_JOURNEY_KEYS)[number];

export interface CoreJourneyStatus {
  keys: Record<CoreJourneyKey, boolean>;
  /** All four keys are true (or core_journey_complete was already recorded). */
  complete: boolean;
  /** core_journey_complete is already stored. */
  recorded: boolean;
}

/**
 * The spec §5 core journey (workbook v2.38 Milestones). Complete only when all four keys are true:
 *
 * - score_first_submitted: any Score submitted to the server, or S08's completed row.
 * - buffer_explanation_presented: B01's completed row (B01 backfills it for established Score
 *   history).
 * - score_plan_relationship_resolved: B02 or H02D presented (their completed rows), the Plan edit
 *   surface entered while a current Score exists (its own milestone row - recordRelationshipResolved),
 *   or a qualifying Plan saved while a first Score exists - which, together with B01, also covers
 *   "B01 presented when a qualifying saved Plan already exists".
 * - qualifying_first_plan_saved: a saved Plan with an Activity Card and a Safety Card (checked on the
 *   server, so existing users count), or the completed P11 / P12 row.
 *
 * `sync` stores `core_journey_complete` the first time the predicate holds. It retires the H02 Home
 * cards, appends the completion sentence to P12, and is what H03 reads. A failed check yields
 * undefined - callers hold back rather than guess.
 */
@Injectable({ providedIn: "root" })
export class CoreJourneyService {
  private recordedInSession = false;
  private relationshipRecordedInSession = false;

  constructor(
    private router: Router,
    private onboardingStepService: OnboardingStepService,
    private interactionTracking: InteractionTrackingService,
    paymentsService: PaymentsService
  ) {
    // Logout emits false - a different account signing in on the same tab gets its own state.
    paymentsService.subscriptionActive$.pipe(filter((active) => !active)).subscribe(() => this.reset());
  }

  /**
   * The predicate now. `assume` marks keys already known true that the server may not reflect yet -
   * e.g. qualifying_first_plan_saved straight after the confirmed save that made it true.
   */
  status(assume: Partial<Record<CoreJourneyKey, boolean>> = {}): Observable<CoreJourneyStatus | undefined> {
    return forkJoin([
      this.onboardingStepService.getSteps(),
      this.onboardingStepService.hasSubmittedScore(),
      this.onboardingStepService.hasQualifyingSavedPlan(),
    ]).pipe(
      map(([steps, hasScore, hasQualifyingPlan]) => {
        if (steps === undefined || hasScore === null || hasQualifyingPlan === null) {
          return undefined;
        }
        const done = (key: string) => !!steps.find((s: OnboardingStep) => s.stepKey === key)?.completedAt;
        const scoreFirstSubmitted = !!assume.score_first_submitted || hasScore || done("S08");
        const qualifying =
          !!assume.qualifying_first_plan_saved || hasQualifyingPlan || ["P11", "P12A", "P12B", "P12C"].some(done);
        const keys: Record<CoreJourneyKey, boolean> = {
          score_first_submitted: scoreFirstSubmitted,
          buffer_explanation_presented: !!assume.buffer_explanation_presented || done("B01"),
          score_plan_relationship_resolved:
            !!assume.score_plan_relationship_resolved ||
            done("B02") ||
            done("H02D") ||
            done(SCORE_PLAN_RELATIONSHIP_RESOLVED_KEY) ||
            (qualifying && scoreFirstSubmitted),
          qualifying_first_plan_saved: qualifying,
        };
        const recorded = this.recordedInSession || done(CORE_JOURNEY_COMPLETE_KEY);
        return { keys, complete: recorded || CORE_JOURNEY_KEYS.every((k) => keys[k]), recorded };
      })
    );
  }

  /** The predicate, storing core_journey_complete the first time it holds. */
  sync(assume: Partial<Record<CoreJourneyKey, boolean>> = {}): Observable<CoreJourneyStatus | undefined> {
    return this.status(assume).pipe(
      tap((status) => {
        if (status?.complete && !status.recorded) {
          this.recordedInSession = true;
          this.onboardingStepService.upsertStep({ stepKey: CORE_JOURNEY_COMPLETE_KEY, completedAt: new Date() });
          this.track(`milestone: ${CORE_JOURNEY_COMPLETE_KEY}`);
        }
      })
    );
  }

  /**
   * The first Plan edit surface was entered while a current Score exists - resolves
   * score_plan_relationship_resolved (workbook v2.38 Milestones). Written once.
   */
  recordRelationshipResolved(): void {
    if (this.relationshipRecordedInSession) {
      return;
    }
    this.relationshipRecordedInSession = true;
    this.onboardingStepService
      .getStep(SCORE_PLAN_RELATIONSHIP_RESOLVED_KEY)
      .pipe(take(1))
      .subscribe((step) => {
        if (step === undefined) {
          // The check failed - a later Plan entry tries again.
          this.relationshipRecordedInSession = false;
          return;
        }
        if (!step?.completedAt) {
          this.onboardingStepService.upsertStep({ stepKey: SCORE_PLAN_RELATIONSHIP_RESOLVED_KEY, completedAt: new Date() });
          this.track(`milestone: ${SCORE_PLAN_RELATIONSHIP_RESOLVED_KEY}; via: plan_edit_with_current_score`);
        }
      });
  }

  private reset(): void {
    this.recordedInSession = false;
    this.relationshipRecordedInSession = false;
  }

  private track(detail: string): void {
    this.interactionTracking.track({
      screen: this.router.url,
      eventName: "onboarding_milestone_recorded",
      interactionType: InteractionType.Complete,
      entityType: "OnboardingMilestone",
      detail,
    });
  }
}
